import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ title, description, keywords, canonical, ogImage, pageType = 'website', jsonLd }) => {
  const siteName = 'RESILIGENT';
  const fullTitle = title.includes('RESILIGENT') ? title : `${title} | ${siteName}`;
  const baseUrl = 'https://resiligent.com';
  
  // Default OG image - RESILIGENT branded preview
  const defaultOgImage = '/opt/photo/resiligent-website1.webp';
  const ogImageUrl = ogImage || defaultOgImage;
  
  // Enhanced default keywords for African market dominance
  const defaultKeywords = 'Nigeria, Africa, Lagos, Ghana, Kenya, South Africa, West Africa, leadership development, executive coaching, organisational development, ICF certified coach, management consulting Africa';
  const fullKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  // Absolute canonical: pass-through if already absolute (e.g. external impactasportal.com),
  // otherwise prefix the resiligent.com base.
  const canonicalHref = canonical
    ? (canonical.startsWith('http') ? canonical : `${baseUrl}${canonical}`)
    : null;

  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : null;

  // react-helmet-async only applies <title> in this environment; meta/link/script children
  // never reach the DOM. Sync the SEO-critical tags imperatively so crawlers read them.
  useEffect(() => {
    const upsertMeta = (attr, key, content) => {
      if (content == null) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const upsertLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', fullKeywords);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    if (canonicalHref) {
      upsertLink('canonical', canonicalHref);
      upsertMeta('property', 'og:url', canonicalHref);
    }

    // Page-specific JSON-LD (e.g. homepage Organization schema)
    document.head.querySelectorAll('script[data-seo-jsonld="1"]').forEach((s) => s.remove());
    if (jsonLdString) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', '1');
      s.textContent = jsonLdString;
      document.head.appendChild(s);
    }

    return () => {
      document.head.querySelectorAll('script[data-seo-jsonld="1"]').forEach((s) => s.remove());
    };
  }, [fullTitle, description, fullKeywords, canonicalHref, jsonLdString]);
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={fullKeywords} />
      
      {/* Robots and indexing */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Geo targeting for Nigeria and Africa */}
      <meta name="geo.region" content="NG" />
      <meta name="geo.placename" content="Lagos, Nigeria" />
      <meta name="geo.position" content="6.5244;3.3792" />
      <meta name="ICBM" content="6.5244, 3.3792" />
      
      {/* Extended geo coverage for African market */}
      <meta name="DC.coverage" content="Nigeria, Ghana, Kenya, South Africa, Africa" />
      <meta name="distribution" content="global" />
      <meta name="target" content="Nigeria, Africa, UK, Europe" />
      
      {/* Language and locale */}
      <meta name="language" content="English" />
      <meta httpEquiv="content-language" content="en-ng" />
      
      {/* Author and publisher */}
      <meta name="author" content="RESILIGENT" />
      <meta name="publisher" content="RESILIGENT - Leadership Development Africa" />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={pageType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_NG" />
      <meta property="og:locale:alternate" content="en_GB" />
      <meta property="og:locale:alternate" content="en_GH" />
      <meta property="og:locale:alternate" content="en_KE" />
      <meta property="og:locale:alternate" content="en_ZA" />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="RESILIGENT - Nigeria's Leading Leadership Development Firm" />
      {canonicalHref && <meta property="og:url" content={canonicalHref} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:site" content="@resiligent" />
      
      {/* Canonical URL */}
      {canonicalHref && <link rel="canonical" href={canonicalHref} />}
      
      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "RESILIGENT",
          "alternateName": "RESILIGENT Leadership Development",
          "description": "Nigeria's leading advisory-led leadership development, executive coaching, and organisational transformation firm serving Africa, UK, and Europe",
          "url": "https://resiligent.com",
          "logo": "https://resiligent.com/logo.png",
          "image": ogImageUrl,
          "telephone": "+234-XXX-XXX-XXXX",
          "priceRange": "$$$$",
          "areaServed": [
            { "@type": "Country", "name": "Nigeria" },
            { "@type": "Country", "name": "Ghana" },
            { "@type": "Country", "name": "Kenya" },
            { "@type": "Country", "name": "South Africa" },
            { "@type": "Country", "name": "United Kingdom" },
            { "@type": "Continent", "name": "Africa" }
          ],
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "NG",
            "addressLocality": "Lagos",
            "addressRegion": "Lagos State"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "6.5244",
            "longitude": "3.3792"
          },
          "sameAs": [
            "https://www.linkedin.com/company/resiligent",
            "https://twitter.com/resiligent"
          ],
          "knowsAbout": [
            "Leadership Development",
            "Executive Coaching",
            "ICF MCC Coaching",
            "Organisational Development",
            "Board Advisory",
            "Assessment Centres",
            "Graduate Recruitment",
            "Succession Planning",
            "Leadership Diagnostics",
            "360 Degree Feedback",
            "Culture Transformation"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "ICF MCC",
              "name": "ICF Master Certified Coach"
            },
            {
              "@type": "EducationalOccupationalCredential", 
              "credentialCategory": "ICF PCC",
              "name": "ICF Professional Certified Coach"
            }
          ]
        })}
      </script>
      
      {/* Local Business Schema for Nigerian market */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "RESILIGENT Lagos",
          "description": "Executive coaching and leadership development firm in Lagos, Nigeria",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Lagos",
            "addressRegion": "Lagos State",
            "addressCountry": "NG"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "6.5244",
            "longitude": "3.3792"
          },
          "url": "https://resiligent.com",
          "areaServed": "Nigeria"
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
